/**
 * Story #6490 (Bug #4838) 8.3 — app-wide banner for the key-status gate's 403.
 * services/api.ts dispatches KEY_REVOKED_EVENT on a 403 {error: 'key_revoked'};
 * this names the paused workspace instead of showing a generic error.
 */
import { useEffect, useState } from 'react'
import { AlertTriangle, X } from 'lucide-react'
import { KEY_REVOKED_EVENT } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'

export default function KeyRevokedBanner() {
  const { user } = useAuth()
  const [notice, setNotice] = useState<{ tenant: string | null; reason: string } | null>(null)

  // A different (or no) signed-in user never sees the previous user's notice.
  useEffect(() => { setNotice(null) }, [user?.id])

  useEffect(() => {
    const onRevoked = (e: Event) => setNotice((e as CustomEvent).detail)
    window.addEventListener(KEY_REVOKED_EVENT, onRevoked)
    return () => window.removeEventListener(KEY_REVOKED_EVENT, onRevoked)
  }, [])

  if (!notice || !user) return null
  const memberships = (user as { org_memberships?: Array<{ org_id: string; org_name?: string }> } | null)?.org_memberships ?? []
  const orgName = notice.tenant ? memberships.find((m) => m.org_id === notice.tenant)?.org_name : null
  const who = orgName || (notice.tenant ? 'this organisation' : 'your workspace')
  return (
    <div role="alert" data-testid="key-revoked-banner"
         className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-start gap-2 text-sm text-amber-900">
      <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
      <span className="flex-1">Search and analysis are paused for {who} because its encryption key was revoked or is unavailable.</span>
      <button type="button" aria-label="Dismiss" onClick={() => setNotice(null)} className="text-amber-700 hover:text-amber-900">
        <X size={14} />
      </button>
    </div>
  )
}
